// Filter bar + the four content views (Conversation, Files, Links,
// Tickets) the channel header's TabSwitcher swaps between via
// `root.activeTab`. The tab bar itself lives in Main.js — it uses
// SharedViewSwitcher from shared-gov.

import { collectAttachments, collectLinks, collectTickets, collectAuthors } from '../../functions/chat/messageScans.js'

// Cache the per-channel "no threads" filter so the Files / Links /
// Tickets collectors can hit the messageScans WeakMap caches with a
// stable array reference. Keyed off the source `root.messagesByChannel`
// reference + active channel id; rebuilds whenever either changes.
const _channelMessagesCache = new WeakMap()
const _channelMessages = (root) => {
  if (!root || !root.activeChannelId) return _EMPTY
  const map = root.messagesByChannel || {}
  const src = map[root.activeChannelId]
  if (!src || !src.length) return _EMPTY
  let cached = _channelMessagesCache.get(map)
  if (!cached) { cached = {}; _channelMessagesCache.set(map, cached) }
  const slot = cached[root.activeChannelId]
  if (slot && slot.src === src) return slot.list
  const list = src.filter(m => !m.threadRoot)
  cached[root.activeChannelId] = { src, list }
  return list
}
const _EMPTY = Object.freeze([])

// ── Filter bar (Conversation tab only) ──────────────────────────────────
export const ChatFilterBar = {
  hide: (el, s) => !(s && s.root && s.root.activeTab === 'conversation' && s.root.showFilterBar),
  flow: 'x wrap',
  align: 'center left',
  gap: 'Z1',
  padding: 'Z B1',
  borderBottomWidth: '1px',
  borderBottomColor: 'overlay',
  borderBottomStyle: 'solid',
  background: 'overlayStrong',
  flex: '0 0 auto',
  // Anchor for the filters-clear delegate's local input/select reset.
  attr: { 'data-filter-bar': 'true' },

  Search: {
    tag: 'input',
    flex: '1',
    minWidth: 'G',
    padding: 'V Z1',
    round: 'C',
    borderWidth: '1px',
    borderColor: 'line',
    borderStyle: 'solid',
    background: 'overlay',
    color: 'title',
    fontSize: 'Y2',
    placeholder: 'Filter messages in this channel…',
    onInput: (e, el, s) => {
      const root = s && s.root
      if (!root) return
      root.update({ filters: { ...root.filters, query: e.target.value } })
    }
  },

  AuthorSelect: {
    tag: 'select',
    padding: 'V Z',
    round: 'C',
    borderWidth: '1px',
    borderColor: 'line',
    borderStyle: 'solid',
    background: 'overlay',
    color: 'title',
    fontSize: 'Y2',
    cursor: 'pointer',
    onChange: (e, el, s) => {
      const root = s && s.root
      if (!root) return
      root.update({ filters: { ...root.filters, authorEmail: e.target.value || null } })
    },
    childExtends: 'ChatAuthorOption',
    childrenAs: 'state',
    children: (el, s) => {
      const root = s && s.root
      if (!root) return [{ value: '', label: 'All authors' }]
      const msgs = _channelMessages(root)
      const authors = collectAuthors(msgs)
      return [
        { value: '', label: 'All authors' },
        ...authors.map(email => {
          const p = el.call('findPerson', email)
          return { value: email, label: (p && p.name) || email }
        })
      ]
    }
  },

  HasAttachments: {
    extends: 'ChatFilterToggle',
    label: '📎 Attachments',
    field: 'hasAttachments'
  },

  HasMentions: {
    extends: 'ChatFilterToggle',
    label: '@ Mentions',
    field: 'hasMentions'
  },

  ClearBtn: {
    tag: 'button',
    type: 'button',
    padding: 'V Z',
    round: 'C',
    border: 'none',
    background: 'transparent',
    color: 'caption',
    cursor: 'pointer',
    fontSize: 'Y2',
    fontWeight: '600',
    text: 'Clear',
    attr: { 'data-action': 'filters-clear' },
    ':hover': { color: 'title' }
  }
}

export const ChatAuthorOption = {
  tag: 'option',
  attr: {
    value: (el, s) => (s && s.value) || '',
    selected: (el, s) => {
      if (!s || !el || !el.parent) return null
      const root = el.parent.state && el.parent.state.root
      const cur = (root && root.filters && root.filters.authorEmail) || ''
      return s.value === cur ? 'selected' : null
    }
  },
  text: (el, s) => (s && s.label) || ''
}

export const ChatFilterToggle = {
  tag: 'button',
  type: 'button',
  padding: 'V Z',
  round: 'C',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: (el, s) => {
    const f = s && s.root && s.root.filters
    return (f && f[el.field]) ? 'cyan' : 'line'
  },
  background: (el, s) => {
    const f = s && s.root && s.root.filters
    return (f && f[el.field]) ? 'cyan.2' : 'overlay'
  },
  color: (el, s) => {
    const f = s && s.root && s.root.filters
    return (f && f[el.field]) ? 'cyan' : 'caption'
  },
  cursor: 'pointer',
  fontSize: 'Y2',
  fontWeight: '600',
  text: (el) => el.label || '',
  transition: 'all 120ms ease',
  attr: { 'data-filter-toggle': (el) => el.field || '' }
}

// ── Files view ──────────────────────────────────────────────────────────
export const ChatFilesView = {
  hide: (el, s) => !(s && s.root && s.root.activeTab === 'files'),
  flex: '1 1 0',
  minHeight: '0',
  flow: 'y',
  overflow: 'auto',
  padding: 'B B1',
  gap: 'A',

  Empty: {
    if: (el, s) => {
      const root = s && s.root
      if (!root) return true
      return collectAttachments(_channelMessages(root)).length === 0
    },
    flow: 'y',
    align: 'center center',
    flex: '1',
    color: 'caption',
    fontSize: 'A',
    padding: 'C',
    text: 'No files shared in this channel yet.'
  },

  Grid: {
    flow: 'x wrap',
    gap: 'Z1',
    childExtends: 'ChatFileCard',
    childrenAs: 'state',
    children: (el, s) => {
      const root = s && s.root
      if (!root) return []
      return collectAttachments(_channelMessages(root))
    }
  }
}

const _isMediaFile = (mime) => mime && (mime.startsWith('image/') || mime.startsWith('video/'))

export const ChatFileCard = {
  flow: 'y',
  gap: 'V',
  padding: 'Z',
  round: 'Z1',
  background: 'overlay',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'line',
  width: 'I',
  cursor: 'pointer',
  transition: 'border-color 120ms ease, transform 120ms ease',
  ':hover': {
    borderColor: 'overlayStrong',
    transform: 'translateY(-2px)'
  },
  onClick: (e, el, s) => {
    if (!s || !s.url) return
    if (_isMediaFile(s.mime)) {
      s.root.update({ lightbox: { url: s.url, mime: s.mime, name: s.name } })
    } else {
      window.open(s.url, '_blank')
    }
  },

  ImageThumb: {
    hide: (el, s) => !(s && s.mime && s.mime.startsWith('image/')),
    tag: 'img',
    width: '100%',
    height: 'F',
    round: 'Z',
    objectFit: 'cover',
    src: (el, s) => s.url,
    alt: (el, s) => s.name || 'image'
  },

  VideoThumb: {
    hide: (el, s) => !(s && s.mime && s.mime.startsWith('video/')),
    tag: 'video',
    width: '100%',
    height: 'F',
    round: 'Z',
    objectFit: 'cover',
    src: (el, s) => s.url,
    preload: 'metadata'
  },

  IconBlock: {
    hide: (el, s) => !!(s && _isMediaFile(s.mime)),
    flow: 'y',
    align: 'center center',
    height: 'F',
    background: 'overlay',
    round: 'Z',
    Icon: {
      tag: 'span',
      fontSize: 'C',
      text: (el, s) => {
        if (!s) return '📄'
        const m = s.mime || ''
        if (m.startsWith('audio/')) return '🎵'
        if (m.includes('pdf')) return '📕'
        if (m.includes('zip') || m.includes('archive')) return '🗜'
        return '📄'
      }
    }
  },

  Name: {
    tag: 'span',
    fontSize: 'Z2',
    fontWeight: '600',
    color: 'title',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => (s && s.name) || ''
  },

  Footer: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',

    Meta: {
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      flex: '1',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => {
        if (!s) return ''
        const root = s.root
        const person = root && el.call('findPerson', s.authorEmail)
        const who = (person && person.name) || s.authorEmail || ''
        return `${el.call('formatBytes', s.size)} · ${who}`
      }
    },

    GoToMsg: {
      tag: 'button',
      type: 'button',
      text: 'Go to message',
      fontSize: 'Y1',
      fontWeight: '600',
      padding: 'V Z',
      round: 'V',
      borderWidth: '0',
      background: 'transparent',
      color: 'blue',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      ':hover': { background: 'blue.1' },
      onClick: (e, el, s) => {
        e.stopPropagation()
        if (!s || !s.messageId) return
        const msgId = s.messageId
        const root = s.root
        const chId = root.activeChannelId
        root.update({ activeTab: 'conversation' })

        const PAGE = 50
        const tryScroll = (attempt) => {
          if (attempt > 40) return
          const msgEl = document.getElementById('msg-' + msgId)
          if (msgEl) {
            msgEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
            msgEl.style.outline = '2px solid var(--color-blue)'
            msgEl.style.outlineOffset = '2px'
            msgEl.style.borderRadius = '8px'
            setTimeout(() => {
              msgEl.style.outline = ''
              msgEl.style.outlineOffset = ''
              msgEl.style.borderRadius = ''
            }, 2000)
            return
          }
          // Expand visible count directly via state
          const counts = root.chatVisibleCounts || {}
          const cur = counts[chId] || PAGE
          const map = root.messagesByChannel || {}
          const total = (map[chId] || []).filter(m => !m.threadRoot).length
          if (cur < total) {
            root.update({ chatVisibleCounts: { ...counts, [chId]: Math.min(cur + PAGE, total) } })
          }
          requestAnimationFrame(() => setTimeout(() => tryScroll(attempt + 1), 50))
        }
        requestAnimationFrame(() => tryScroll(0))
      }
    }
  }
}

// ── Links view ──────────────────────────────────────────────────────────
export const ChatLinksView = {
  hide: (el, s) => !(s && s.root && s.root.activeTab === 'links'),
  flex: '1 1 0',
  minHeight: '0',
  flow: 'y',
  overflow: 'auto',
  padding: 'B B1',
  gap: 'V',

  Empty: {
    if: (el, s) => {
      const root = s && s.root
      if (!root) return true
      return collectLinks(_channelMessages(root)).length === 0
    },
    flow: 'y',
    align: 'center center',
    flex: '1',
    color: 'caption',
    fontSize: 'A',
    padding: 'C',
    text: 'No links shared in this channel yet.'
  },

  List: {
    flow: 'y',
    gap: 'V',
    childExtends: 'ChatLinkRow',
    childrenAs: 'state',
    children: (el, s) => {
      const root = s && s.root
      if (!root) return []
      return collectLinks(_channelMessages(root))
    }
  }
}

export const ChatLinkRow = {
  flow: 'y',
  gap: 'V',
  padding: 'Z A',
  round: 'Z',
  cursor: 'pointer',
  background: 'overlay',
  borderLeftWidth: '2px',
  borderLeftStyle: 'solid',
  borderLeftColor: 'cyan',
  transition: 'background 120ms ease',
  ':hover': { background: 'line' },
  attr: { 'data-link': (el, s) => (s && s.url) || '' },

  Url: {
    tag: 'span',
    fontSize: 'Z2',
    fontWeight: '600',
    color: 'cyan',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => (s && s.url) || ''
  },
  Meta: {
    tag: 'span',
    fontSize: 'Y2',
    color: 'caption',
    text: (el, s) => {
      if (!s) return ''
      const root = s.root
      const person = root && el.call('findPerson', s.authorEmail)
      const who = (person && person.name) || s.authorEmail || ''
      const when = s.createdAt ? `${el.call('dayLabel', s.createdAt)} · ${el.call('clockTime', s.createdAt)}` : ''
      return `${who} · ${when}`
    }
  }
}

// ── Tickets view ────────────────────────────────────────────────────────
export const ChatTicketsView = {
  hide: (el, s) => !(s && s.root && s.root.activeTab === 'tickets'),
  flex: '1 1 0',
  minHeight: '0',
  flow: 'y',
  overflow: 'auto',
  padding: 'B B1',
  gap: 'V',

  Empty: {
    if: (el, s) => {
      const root = s && s.root
      if (!root) return true
      return collectTickets(_channelMessages(root)).length === 0
    },
    flow: 'y',
    align: 'center center',
    flex: '1',
    color: 'caption',
    fontSize: 'A',
    padding: 'C',
    text: 'No tickets referenced in this channel yet. Use #PROJECT-123 syntax to link tickets.'
  },

  List: {
    flow: 'y',
    gap: 'V',
    childExtends: 'ChatTicketRow',
    childrenAs: 'state',
    children: (el, s) => {
      const root = s && s.root
      if (!root) return []
      return collectTickets(_channelMessages(root))
    }
  }
}

export const ChatTicketRow = {
  flow: 'x',
  gap: 'A',
  align: 'center left',
  padding: 'Z A',
  round: 'Z',
  cursor: 'pointer',
  background: 'overlay',
  transition: 'background 120ms ease',
  ':hover': { background: 'line' },

  Tag: {
    tag: 'span',
    padding: 'V Z',
    round: 'C',
    background: 'cyan.2',
    color: 'cyan',
    fontSize: 'Y2',
    fontWeight: '700',
    fontFamily: 'monospace',
    text: (el, s) => (s && s.ref) ? '#' + s.ref : ''
  },
  Body: {
    tag: 'span',
    flex: '1',
    fontSize: 'Z2',
    color: 'subtitle',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => (s && s.body) || ''
  },
  Meta: {
    tag: 'span',
    fontSize: 'Y2',
    color: 'caption',
    text: (el, s) => {
      if (!s) return ''
      const root = s.root
      const person = root && el.call('findPerson', s.authorEmail)
      return (person && person.name) || s.authorEmail || ''
    }
  }
}
